import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import MovieCard from '@/components/feature/MovieCard';
import EmptyState from '@/components/feature/EmptyState';
import { movies, categories, regions, years, ratingOptions, sortOptions } from '@/mocks/movies';

const PAGE_SIZE = 12;

function parseRating(label: string): number {
  const match = label.match(/(\d+)/);
  return match ? Number(match[1]) : 0;
}

function isYearMatch(movieYear: number, option: string): boolean {
  if (option === '全部') return true;
  if (option === '2020及更早') return movieYear <= 2020;
  return movieYear === Number(option);
}

export default function Movies() {
  const [searchParams] = useSearchParams();
  const [category, setCategory] = useState('全部');
  const [year, setYear] = useState('全部');
  const [region, setRegion] = useState('全部');
  const [rating, setRating] = useState('全部');
  const [sort, setSort] = useState('最新上映');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    const c = searchParams.get('category');
    if (c) setCategory(c);
  }, [searchParams]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [category, year, region, rating, sort]);

  const filtered = useMemo(() => {
    const list = movies.filter((m) => {
      const catOk = category === '全部' || m.category === category || m.genres.includes(category);
      const yearOk = isYearMatch(m.year, year);
      const regionOk = region === '全部' || m.region === region;
      const ratingOk = rating === '全部' || m.rating >= parseRating(rating);
      return catOk && yearOk && regionOk && ratingOk;
    });
    if (sort === '评分最高') list.sort((a, b) => b.rating - a.rating);
    else if (sort === '热门程度') list.sort((a, b) => b.popularity - a.popularity);
    else list.sort((a, b) => b.year - a.year || b.popularity - a.popularity);
    return list;
  }, [category, year, region, rating, sort]);

  const shown = filtered.slice(0, visibleCount);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(totalPages, Math.ceil(visibleCount / PAGE_SIZE));

  const reset = () => {
    setCategory('全部');
    setYear('全部');
    setRegion('全部');
    setRating('全部');
    setSort('最新上映');
  };

  const hasActiveFilter = category !== '全部' || year !== '全部' || region !== '全部' || rating !== '全部';

  const FilterGroups = () => (
    <div className="space-y-6">
      <FilterGroup label="类型" options={categories} value={category} onChange={setCategory} />
      <FilterGroup label="年份" options={years} value={year} onChange={setYear} />
      <FilterGroup label="地区" options={regions} value={region} onChange={setRegion} />
      <FilterGroup label="评分" options={ratingOptions} value={rating} onChange={setRating} />
      <button
        type="button"
        onClick={reset}
        className="w-full py-2 rounded-md border border-background-400/60 hover:border-primary-500 text-foreground-800 text-sm transition-colors whitespace-nowrap cursor-pointer"
      >
        重置筛选条件
      </button>
    </div>
  );

  return (
    <div className="w-full bg-background-50">
      <div className="w-full px-4 md:px-8 py-8 md:py-10">
        <div className="max-w-screen mx-auto">
          <nav className="text-xs md:text-sm text-foreground-600 mb-4 flex items-center gap-1.5">
            <Link to="/" className="hover:text-primary-400 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-800">电影分类</span>
          </nav>

          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-black text-foreground-950 mb-2">电影分类浏览</h1>
            <p className="text-sm text-foreground-600 max-w-3xl">
              按类型、年份、地区与评分多维度筛选高清电影资源，支持最新上映、评分高低、热门程度多种排序方式，帮你快速找到想看的影片。
            </p>
          </div>

          <div className="flex flex-col lg:flex-row gap-8">
            <aside className="hidden lg:block w-64 shrink-0">
              <div className="bg-background-100 border border-background-300/50 rounded-lg p-5 sticky top-24">
                <h2 className="text-sm font-bold text-foreground-950 mb-4 flex items-center gap-2">
                  <i className="ri-filter-3-line text-primary-500"></i> 筛选条件
                </h2>
                <FilterGroups />
              </div>
            </aside>

            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
                <p className="text-sm text-foreground-600">
                  共找到 <span className="text-primary-400 font-semibold">{filtered.length}</span> 部影片
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setDrawerOpen(true)}
                    className="lg:hidden px-3 py-2 rounded-md bg-background-100 border border-background-300/60 text-foreground-800 text-sm flex items-center gap-1.5 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-filter-3-line"></i> 筛选
                    {hasActiveFilter && <span className="w-1.5 h-1.5 rounded-full bg-primary-500"></span>}
                  </button>
                  <div className="relative">
                    <select
                      value={sort}
                      onChange={(e) => setSort(e.target.value)}
                      className="appearance-none pl-3 pr-8 py-2 rounded-md bg-background-100 border border-background-300/60 text-foreground-800 text-sm outline-none focus:border-primary-400 cursor-pointer"
                      aria-label="排序方式"
                    >
                      {sortOptions.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-2 top-1/2 -translate-y-1/2 text-foreground-500 pointer-events-none"></i>
                  </div>
                </div>
              </div>

              {shown.length === 0 ? (
                <div className="bg-background-100 border border-background-300/50 rounded-lg">
                  <EmptyState
                    icon="ri-film-line"
                    title="暂无符合条件的电影"
                    description="试试放宽筛选条件，或重置后重新浏览全部影片。"
                    actionLabel="重置筛选"
                    onAction={reset}
                  />
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
                    {shown.map((movie) => (
                      <MovieCard key={movie.id} movie={movie} />
                    ))}
                  </div>

                  <div className="mt-8 flex flex-col items-center gap-4">
                    {visibleCount < filtered.length && (
                      <button
                        type="button"
                        onClick={() => setVisibleCount((v) => v + PAGE_SIZE)}
                        className="px-6 py-2.5 rounded-full bg-background-100 border border-background-300/60 hover:border-primary-500 text-foreground-800 text-sm transition-colors whitespace-nowrap cursor-pointer"
                      >
                        加载更多影片
                      </button>
                    )}
                    {totalPages > 1 && (
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => setVisibleCount(Math.max(PAGE_SIZE, (currentPage - 1) * PAGE_SIZE))}
                          disabled={currentPage === 1}
                          className="w-9 h-9 rounded-md bg-background-100 border border-background-300/60 text-foreground-800 flex items-center justify-center disabled:opacity-40 hover:border-primary-500 transition-colors cursor-pointer"
                          aria-label="上一页"
                        >
                          <i className="ri-arrow-left-s-line"></i>
                        </button>
                        {Array.from({ length: totalPages }).map((_, i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={() => setVisibleCount((i + 1) * PAGE_SIZE)}
                            className={`w-9 h-9 rounded-md text-sm flex items-center justify-center transition-colors cursor-pointer ${
                              currentPage === i + 1
                                ? 'bg-primary-500 text-background-50'
                                : 'bg-background-100 border border-background-300/60 text-foreground-800 hover:border-primary-500'
                            }`}
                          >
                            {i + 1}
                          </button>
                        ))}
                        <button
                          type="button"
                          onClick={() => setVisibleCount((currentPage + 1) * PAGE_SIZE)}
                          disabled={currentPage === totalPages}
                          className="w-9 h-9 rounded-md bg-background-100 border border-background-300/60 text-foreground-800 flex items-center justify-center disabled:opacity-40 hover:border-primary-500 transition-colors cursor-pointer"
                          aria-label="下一页"
                        >
                          <i className="ri-arrow-right-s-line"></i>
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-[55]">
          <div className="absolute inset-0 bg-black/60 animate-fade-in" onClick={() => setDrawerOpen(false)}></div>
          <div className="absolute top-0 left-0 bottom-0 w-[85%] max-w-sm bg-background-50 border-r border-background-300/50 overflow-y-auto animate-modal-in">
            <div className="flex items-center justify-between px-5 py-4 border-b border-background-300/50 sticky top-0 bg-background-50">
              <h2 className="text-base font-bold text-foreground-950 flex items-center gap-2">
                <i className="ri-filter-3-line text-primary-500"></i> 筛选条件
              </h2>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                aria-label="关闭筛选"
                className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-800 hover:bg-background-200/60 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="p-5">
              <FilterGroups />
            </div>
            <div className="px-5 pb-6">
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="w-full py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
              >
                查看 {filtered.length} 部影片
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FilterGroup({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: string[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <p className="text-xs font-semibold text-foreground-700 mb-2.5">{label}</p>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt)}
            className={`px-3 py-1.5 rounded-md text-xs transition-colors whitespace-nowrap cursor-pointer ${
              value === opt
                ? 'bg-primary-500 text-background-50'
                : 'bg-background-200/60 text-foreground-700 hover:text-primary-400'
            }`}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  );
}